import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import { cloneDeep, isEmpty } from "lodash";

import type {
    EquipmentFunctionalityClass,
    IEquipmentFunctionalityType,
    IEquipmentFunctionalityTypeSlim,
    IManufacturer,
    IResponseWithLoading,
    IrMap,
} from "App/Modules/Models";
import { EquipmentFunctionalityClassFunctions } from "App/Modules/Models";
import type { IEquipmentFunctionalityTypesRepository, IManufacturersRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditFunctionalityTypeInformationComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "irManufacturersRepository",
        "irEquipmentFunctionalityTypesRepository",
    ];

    public resolve: IFormContainerResolve<IEquipmentFunctionalityTypeSlim, void>;

    public editResponse: IResponseWithLoading<IEquipmentFunctionalityType>;

    public deleteResponse: IResponseWithLoading<void>;

    public equipmentFunctionalityType: IEquipmentFunctionalityTypeSlim;

    public originalEquipmentFunctionalityType: IEquipmentFunctionalityTypeSlim;

    public manufacturers: IResponseWithLoading<IManufacturer[]>;

    public equipmentFunctionalityClasses: IrMap<EquipmentFunctionalityClass, string>;

    constructor(
        private readonly $scope: IScope,
        private readonly manufacturersRepository: IManufacturersRepository,
        private readonly equipmentFunctionalityTypesRepository: IEquipmentFunctionalityTypesRepository,
    ) {}

    public $onInit() {
        this.originalEquipmentFunctionalityType = this.resolve.formData;
        this.equipmentFunctionalityType = cloneDeep(this.originalEquipmentFunctionalityType);
        extendWithLoadingAndFail(this.manufacturersRepository.getManufacturers())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IManufacturer[]>) => {
                    this.manufacturers = response;
                },
                (error) => {
                    this.manufacturers = error;
                },
            );

        this.equipmentFunctionalityClasses =
            EquipmentFunctionalityClassFunctions.GetEquipmentFunctionalityClassDictionary();
    }

    public editEquipmentFunctionalityType() {
        const patchObject = {} as IEquipmentFunctionalityTypeSlim;

        if (
            this.originalEquipmentFunctionalityType.manufacturer.id !== this.equipmentFunctionalityType.manufacturer.id
        ) {
            patchObject.manufacturer = this.equipmentFunctionalityType.manufacturer;
        }

        if (this.originalEquipmentFunctionalityType.model !== this.equipmentFunctionalityType.model) {
            patchObject.model = this.equipmentFunctionalityType.model;
        }

        if (this.originalEquipmentFunctionalityType.version !== this.equipmentFunctionalityType.version) {
            patchObject.version = this.equipmentFunctionalityType.version;
        }

        if (this.originalEquipmentFunctionalityType.description !== this.equipmentFunctionalityType.description) {
            patchObject.description = this.equipmentFunctionalityType.description;
        }

        if (this.originalEquipmentFunctionalityType.class !== this.equipmentFunctionalityType.class) {
            patchObject.class = this.equipmentFunctionalityType.class;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.equipmentFunctionalityTypesRepository.patchEquipmentFunctionalityType(
                    this.equipmentFunctionalityType.id,
                    patchObject,
                ),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IEquipmentFunctionalityType>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public deleteEquipmentFunctionalityType() {
        extendWithLoadingAndFail(
            this.equipmentFunctionalityTypesRepository.deleteEquipmentFunctionalityType(
                this.equipmentFunctionalityType.id,
            ),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;
                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (errorResponse) => {
                    this.editResponse = errorResponse;
                },
            );
    }
}
